import { useEffect, useState } from 'react';

import { loadTemplateSession } from './planTemplateSource';
import { useLocale } from '../primitives/useLocale';
import type { ProgramApiFetch } from '../ProgramEditor/types';

// Render-time resolver for the workout-level coach notes (program
// description + the day's session note). FK + resolve at render — the golden
// rule (feedback_no_denormalized_display_snapshots): the notes live on the
// program/plan template, never snapshotted onto the session.
//
// `draft.coachNotes` is captured at Start for fresh 3.14+ launches (instant,
// works offline). This hook is the FALLBACK that makes the "?" appear for
// sessions that were started before the field existed, resumed, or opened in
// edit mode — anything carrying a `fromPlanId` but no captured coachNotes.
// The template itself comes from planTemplateSource (cache-first, then the
// server — LG-30), the same single source usePlanCues reads.
type CoachNotes = { programDescription?: string; sessionNote?: string };

const hasContent = (n: CoachNotes | undefined): n is CoachNotes =>
  Boolean(n && (n.programDescription || n.sessionNote));

export function useCoachNotes(
  fromPlanId: string | undefined,
  captured: CoachNotes | undefined,
  apiFetch: ProgramApiFetch,
): CoachNotes | undefined {
  const lang = useLocale();
  const [resolved, setResolved] = useState<CoachNotes | undefined>(undefined);

  useEffect(() => {
    // Captured-at-start wins (instant + offline-safe); skip the lookup.
    if (hasContent(captured) || !fromPlanId) {
      setResolved(undefined);
      return;
    }
    let alive = true;
    void (async () => {
      const session = await loadTemplateSession(fromPlanId, apiFetch, lang);
      if (!alive || !session) return;
      const next: CoachNotes = {
        ...(session.programDescription
          ? { programDescription: session.programDescription }
          : {}),
        ...(session.sessionNotes ? { sessionNote: session.sessionNotes } : {}),
      };
      if (hasContent(next)) setResolved(next);
    })();
    return () => {
      alive = false;
    };
  }, [fromPlanId, captured, apiFetch, lang]);

  return hasContent(captured) ? captured : resolved;
}
